/**
 *
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : FullStack Developer
 * @Email : kpozos@ibl.mx
 *
 */

import {
    getDateOnly,
    getStartOfDayTimestamp,
    getTimeOnly
} from "../../../../../../../Apis/functions";
import {STATUS_MODEL} from "../../../../../../../enums/statusEnum";
import {ATTENDANCE_STATUS_MODEL} from "../../../Model/Model";
import {DEVICE_ACCESS_TYPE_MODEL} from "../../../../../Devices/Model/Model";
import {PERIOD_TIME_UNIT_ENUM} from "../../../../../../../enums/PeriodTimeUnitEnum";
import {buildBatchApprovalRows} from "../../Request/Model/Model";
import {
    endOfDay,
    endOfMonth,
    endOfWeek,
    endOfYear,
    startOfDay,
    startOfMonth,
    startOfWeek,
    startOfYear,
    subDays
} from "date-fns";

export const SUBMISSION_PERIOD_REVIEW_FILTER_MODEL ={
    departmentId :null
}

export const SUBMISSION_PERIOD_REVIEW_ITEM_MODEL ={
    id: null,
    description: null,
    evidence: null,
    items: null,
    toRegister: function ({ files }) {
        const formData = new FormData();
        formData.append('description', this.description);
        if (Array.isArray(files)) {
            files.forEach((e, index) => {
                formData.append(`evidence[${index}].fileUploadDto.files`, e.fileSelected);
                formData.append(`evidence[${index}].fileUploadDto.userFileNames`, e.fileSelected.name);
            });
        } else if (files) {
            formData.append('evidence[0].fileUploadDto.files', files.fileSelected);
            formData.append('evidence[0].fileUploadDto.userFileNames', files.fileSelected.name);
        }
        return formData;
    },
    isValid: function (){
        return this.description !== null && this.description !== "";
    },
    toRegisterChangeRequest: function ({ files, items }) {
        const formData = new FormData();
        formData.append('id', this.id);
        formData.append('description', this.description);
        if (Array.isArray(files)) {
            files.forEach((e, index) => {
                if(e.id){
                    formData.append(`evidence[${index}].id`, e.id);
                }else{
                    formData.append(`evidence[${index}].fileUploadDto.files`, e.fileSelected);
                    formData.append(`evidence[${index}].fileUploadDto.userFileNames`, e.fileSelected.name);
                }
            });
        } else if (files) {
            if(files.id){
                formData.append(`evidence[0].id`, files.id);
            }else{
                formData.append('evidence[0].fileUploadDto.files', files.fileSelected);
                formData.append('evidence[0].fileUploadDto.userFileNames', files.fileSelected.name);
            }
        }

        // A row can carry an entry and an exit adjustment, and each one is its own
        // item; pairing them by row would drop the exit and could send an id with
        // the datetime of the other side.
        let index = 0;
        items.forEach((e) => {
            if (e.entryItemId && e.entryFullAdjusted) {
                formData.append(`items[${index}].id`, e.entryItemId);
                formData.append(`items[${index}].dateAdjusted`, e.entryFullAdjusted);
                index++;
            }
            if (e.exitItemId && e.exitFullAdjusted) {
                formData.append(`items[${index}].id`, e.exitItemId);
                formData.append(`items[${index}].dateAdjusted`, e.exitFullAdjusted);
                index++;
            }
        });

        return formData;
    },
    isValidChangeRequest: function (items){
        return this.description !== null && this.description !== "" && items.length > 0;
    },
    toSet: function (data){
        return {
            ...this,
            id: data.id,
            description: data.description
        }
    }
}

export const ROW_ATTENDANCE_ADJUSTED = {
    fullName: null,
    departmentName: null,
    attendanceId: null,
    shiftStart: null,
    shiftEnd: null,
    entryItemId: null,
    entryDateOriginal: null,
    entryFullAdjusted: null,
    entryDateAdjusted: null,
    entryTimeAdjusted: null,
    entryStatusAdjusted: null,
    entryDateAdjustedIsValid: true,
    entryTimeAdjustedIsValid: true,
    entryFullAdjustedIsValid: true,
    // Bumped when a save is rejected, to remount the picker and drop its value.
    entryResetVersion: 0,


    exitItemId: null,
    exitDateOriginal: null,
    exitDateAdjusted: null,
    exitTimeAdjusted: null,
    exitFullAdjusted: null,
    exitStatusAdjusted: null,

    exitDateAdjustedIsValid: true,
    exitTimeAdjustedIsValid: true,
    exitFullAdjustedIsValid: true,
    exitResetVersion: 0,

    disabled: false,
    toRegister: function (row, adjustmentType) {
        let toSave = {items: []};
        if(adjustmentType === DEVICE_ACCESS_TYPE_MODEL.ENTRY.name){
            toSave.items.push({
                id: row.entryItemId,
                attendanceId: row.attendanceId,
                dateAdjusted: row.entryFullAdjusted,
                adjustmentType: adjustmentType
            });
        }else if(adjustmentType === DEVICE_ACCESS_TYPE_MODEL.EXIT.name){
            toSave.items.push({
                id: row.exitItemId,
                attendanceId: row.attendanceId,
                dateAdjusted: row.exitFullAdjusted,
                adjustmentType: adjustmentType
            });
        }
        return toSave;
    },
    toSet: function (row, adjustedDisabled = true){
        const entryDateEquals = getDateOnly(row.entryDateOriginal) === getDateOnly(row.entryDateAdjusted);
        const entryTimeEquals = getTimeOnly(row.entryDateOriginal) === getTimeOnly(row.entryDateAdjusted);

        const exitDateEquals = getDateOnly(row.exitDateOriginal) === getDateOnly(row.exitDateAdjusted);
        const exitTimeEquals = getTimeOnly(row.exitDateOriginal) === getTimeOnly(row.exitDateAdjusted);

        return {
            fullName: row.fullName,
            departmentName: row.departmentName,
            attendanceId: row.attendanceId,
            shiftStart: row.shiftStart,
            shiftEnd: row.shiftEnd,

            entryItemId: row.entryItemId,
            entryDateOriginal: row.entryDateOriginal,
            entryFullAdjusted: row.entryDateAdjusted,
            entryDateAdjusted: entryDateEquals && row.entryStatusId !== STATUS_MODEL.DRAFT ? null : getDateOnly(row.entryDateAdjusted),
            entryTimeAdjusted: entryTimeEquals && row.entryStatusId !== STATUS_MODEL.DRAFT ? null : getTimeOnly(row.entryDateAdjusted),
            entryStatusAdjusted: row.entryStatusId,
            entryDateAdjustedIsValid: true,
            entryTimeAdjustedIsValid: true,
            entryFullAdjustedIsValid: true,

            exitItemId: row.exitItemId,
            exitDateOriginal: row.exitDateOriginal,
            exitFullAdjusted: row.exitDateAdjusted,
            exitDateAdjusted: exitDateEquals && row.exitStatusId !== STATUS_MODEL.DRAFT ? null : getDateOnly(row.exitDateAdjusted),
            exitTimeAdjusted: exitTimeEquals && row.exitStatusId !== STATUS_MODEL.DRAFT ? null : getTimeOnly(row.exitDateAdjusted),
            exitStatusAdjusted: row.exitStatusId,

            exitDateAdjustedIsValid: true,
            exitTimeAdjustedIsValid: true,
            exitFullAdjustedIsValid: true,

            disabled: adjustedDisabled && (row.entryStatusId === STATUS_MODEL.ADJUSTED || row.exitStatusId === STATUS_MODEL.ADJUSTED)
        }
    },
    resetEntryProps: function (e){
        return {
            ...e,
            entryItemId: null,
            entryDateOriginal: null,
            entryFullAdjusted: null,
            entryDateAdjusted: null,
            entryTimeAdjusted: null,
            entryStatusAdjusted: null,
            entryDateAdjustedIsValid: true,
            entryTimeAdjustedIsValid: true,
            entryFullAdjustedIsValid: true,
        }
    },
    resetExitProps: function (e){
        return {
            ...e,
            exitItemId: null,
            exitDateOriginal: null,
            exitDateAdjusted: null,
            exitTimeAdjusted: null,
            exitFullAdjusted: null,
            exitStatusAdjusted: null,
            exitDateAdjustedIsValid: true,
            exitTimeAdjustedIsValid: true,
            exitFullAdjustedIsValid: true,
        }
    },
    mapRequestedBatchRows: function (objectItems){ //Confirm changes requested from attendance adjustment table
        return Object.values(objectItems)?.map((e, index) => {
            const entryDateOriginal = e?.entryDateOriginal;
            const entryDateAdjusted = e?.entryFullAdjusted;
            const exitDateOriginal = e?.exitDateOriginal;
            const exitDateAdjusted = e?.exitFullAdjusted;

            return buildBatchApprovalRows({
                index,
                fullName: e?.fullName,
                departmentName: e?.departmentName,
                totalItems: objectItems?.length,
                shiftStart: e?.shiftStart,
                shiftEnd: e?.shiftEnd,
                entryItemId: e?.entryItemId,
                entryDateOriginal,
                entryDateAdjusted,
                exitItemId: e?.exitItemId,
                exitDateOriginal,
                exitDateAdjusted
            });
        });
    },
};

/**
 * Determines attendance status based on total worked time.
 *
 * @param {number|null} totalTime - Calculated time in minutes
 * @param {number} minutesWorked - Expected minutes
 * @returns {number|null} Status ID
 */
/**
 * Tells whether any adjusted row is holding an invalid value.
 *
 * Used to keep the review action disabled while a picker is in error, so an
 * out of shift or inverted datetime cannot be sent for approval.
 *
 * @param {Object} rowsAdjusted - Adjusted rows keyed by attendance id.
 * @returns {boolean} True when at least one row is invalid.
 */
export const hasInvalidAdjustedRows = (rowsAdjusted) =>
    Object.values(rowsAdjusted || {}).some(e =>
        e?.entryDateAdjustedIsValid === false ||
        e?.entryTimeAdjustedIsValid === false ||
        e?.entryFullAdjustedIsValid === false ||
        e?.exitDateAdjustedIsValid === false ||
        e?.exitTimeAdjustedIsValid === false ||
        e?.exitFullAdjustedIsValid === false
    );

/**
 * Normalizes a datetime into a timestamp, so values stored as ISO strings and
 * as numbers can be compared against each other.
 *
 * @param {Date|string|number|null} value - Datetime to normalize.
 * @returns {number|null} The timestamp, or null when it is missing or invalid.
 */
const toTimestamp = (value) => {
    if (value === null || value === undefined) return null;
    const time = new Date(value).getTime();
    return isNaN(time) ? null : time;
};

/**
 * Tells whether any adjusted row actually changes its captured datetime.
 *
 * Picking the very same value still marks the row as a draft, so the status
 * alone is not enough to know that there is something worth reviewing.
 *
 * @param {Object} rowsAdjusted - Adjusted rows keyed by attendance id.
 * @returns {boolean} True when at least one row differs from its original.
 */
export const hasAdjustedChanges = (rowsAdjusted) =>
    Object.values(rowsAdjusted || {}).some(e => {
        const entryChanged = e?.entryStatusAdjusted === STATUS_MODEL.DRAFT &&
            toTimestamp(e?.entryFullAdjusted) !== toTimestamp(e?.entryDateOriginal);

        const exitChanged = e?.exitStatusAdjusted === STATUS_MODEL.DRAFT &&
            toTimestamp(e?.exitFullAdjusted) !== toTimestamp(e?.exitDateOriginal);

        return entryChanged || exitChanged;
    });

export const getTotalTimeStatus = (totalTime, minutesWorked) => {
    if(totalTime == null ) return null;
    return (totalTime >= minutesWorked)? ATTENDANCE_STATUS_MODEL.inTime.id : ATTENDANCE_STATUS_MODEL.afterHours.id;
}


/**
 * Attendance Period Calculator
 * Mirrors backend logic:
 * - Period range calculation (cycle)
 * - Review & approval windows
 * - Deferred shift handling
 */

/**
 * Returns the Monday of the week for a given date.
 *
 * @param {Date|number|string} date
 * @returns {Date}
 */
const getMonday = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = (day === 0 ? -6 : 1) - day;
    d.setDate(d.getDate() + diff);
    return d;
};

/**
 * Adds a period to a date based on unit.
 *
 * @param {Date|number} date
 * @param {number} value
 * @param {string} unit
 * @returns {Date|null}
 */
const addPeriod = (date, value, unit) => {
    let d = new Date(date);

    switch (unit) {
        case PERIOD_TIME_UNIT_ENUM.WEEKS.name:
            d.setDate(d.getDate() + Number(value) * 7);
            break;
        case PERIOD_TIME_UNIT_ENUM.MONTHS.name:
            d.setMonth(d.getMonth() + Number(value));
            break;
        case PERIOD_TIME_UNIT_ENUM.YEARS.name:
            d.setFullYear(d.getFullYear() + Number(value));
            break;
        default:
            d = null;
           break;
    }

    return d;
};

/**
 * Shifts a date forward based on deferred periods.
 *
 * @param {Date} date
 * @param {number} deferred
 * @param {number} frequency
 * @param {string} unit
 * @returns {Date|null}
 */
const shiftToCurrent = (date, deferred, frequency, unit) => {
    let d = new Date(date);

    switch (unit) {
        case PERIOD_TIME_UNIT_ENUM.WEEKS.name:
            d.setDate(d.getDate() + deferred * frequency * 7);
            break;

        case PERIOD_TIME_UNIT_ENUM.MONTHS.name:
            d.setMonth(d.getMonth() + deferred * frequency);
            break;

        case PERIOD_TIME_UNIT_ENUM.YEARS.name:
            d.setFullYear(d.getFullYear() + deferred * frequency);
            break;

        default:
            d = null;
           break;
    }

    return d;
};

/**
 * Calculates the attendance period range.
 *
 * Logic:
 * - Applies deferred periods backwards from today
 * - Determines start based on unit:
 *   - Weeks → Monday
 *   - Months → First day of month
 *   - Years → First day of year
 * - Calculates next period start
 * - Derives end as last millisecond of the period
 *
 * @param {Object} config
 * @param {number} config.frequencyValue
 * @param {number} config.deferredPeriodValue
 * @param {string} config.frequencyTimeUnit
 *
 * @returns {{
 *   startPeriod: number,
 *   endPeriod: number
 * } | null}
 */export const calculatePeriodRange = (config) => {
    const deferred = Number(config?.deferredPeriodValue ?? 0);
    const frequency = Number(config?.frequencyValue ?? 1);
    const unit = config?.frequencyTimeUnit;
    const today = new Date();
    let baseDate;
    let start;

    switch (unit) {
        case PERIOD_TIME_UNIT_ENUM.WEEKS.name:
            baseDate = new Date(today);
            baseDate.setDate(baseDate.getDate() - deferred * frequency * 7);
            break;

        case PERIOD_TIME_UNIT_ENUM.MONTHS.name:
            baseDate = new Date(today);
            baseDate.setMonth(baseDate.getMonth() - deferred * frequency);
            break;

        case PERIOD_TIME_UNIT_ENUM.YEARS.name:
            baseDate = new Date(today);
            baseDate.setFullYear(baseDate.getFullYear() - deferred * frequency);
            break;

        default:
            return null;
    }

    switch (unit) {
        case PERIOD_TIME_UNIT_ENUM.WEEKS.name:
            start = getStartOfDayTimestamp(getMonday(baseDate));
            break;

        case PERIOD_TIME_UNIT_ENUM.MONTHS.name:
            start = getStartOfDayTimestamp(
                new Date(baseDate.getFullYear(), baseDate.getMonth(), 1)
            );
            break;

        case PERIOD_TIME_UNIT_ENUM.YEARS.name:
            start = getStartOfDayTimestamp(
                new Date(baseDate.getFullYear(), 0, 1)
            );
            break;
    }

    if (!start) return null;

    const nextStart = addPeriod(start, frequency, unit);

    const end = new Date(nextStart);
    end.setMilliseconds(end.getMilliseconds() - 1);

    return {
        startPeriod: start,
        endPeriod: end.getTime()
    };
};

/**
 * Calculates supervisor review and admin approval windows.
 *
 * Flow:
 * 1. Computes period range
 * 2. Shifts to current period using deferred logic
 * 3. Builds windows relative to period end
 *
 * Rules:
 * - Start dates use beginning of day (00:00:00)
 * - End dates use end of day (23:59:59)
 * - Offsets are inclusive (value - 1)
 *
 * @param {Object} config
 * @param {number} config.supervisorReviewPeriodFrom
 * @param {number} config.supervisorReviewPeriodTo
 * @param {number} config.adminApprovalPeriodFrom
 * @param {number} config.adminApprovalPeriodTo
 *
 * @returns {{
 *   supervisorReviewStartDate: number,
 *   supervisorReviewEndDate: number,
 *   adminApprovalStartDate: number,
 *   adminApprovalEndDate: number
 * } | null}
 */
const WEEK_OPTIONS = { weekStartsOn: 1 }; // Monday
export const calculateWindows = (config) => {
    const frequency = Number(config?.frequencyValue ?? 1);
    const deferred = Number(config?.deferredPeriodValue ?? 0);
    const unit = config?.frequencyTimeUnit;

    const { endPeriod } = calculatePeriodRange(config);
    if (!endPeriod) return null;

    const currentEnd = shiftToCurrent(
        new Date(endPeriod),
        deferred,
        frequency,
        unit
    );
    if (!currentEnd) return null;

    let periodStart;
    let periodEnd;

    switch (unit) {
        case PERIOD_TIME_UNIT_ENUM.WEEKS.name:
            periodStart = startOfWeek(currentEnd, WEEK_OPTIONS);
            periodEnd = endOfWeek(currentEnd, WEEK_OPTIONS);
            break;

        case PERIOD_TIME_UNIT_ENUM.MONTHS.name:
            periodStart = startOfMonth(currentEnd);
            periodEnd = endOfMonth(currentEnd);
            break;

        case PERIOD_TIME_UNIT_ENUM.YEARS.name:
            periodStart = startOfYear(currentEnd);
            periodEnd = endOfYear(currentEnd);
            break;

        default:
            return null;
    }

    const buildStart = (value) =>
        startOfDay(subDays(periodEnd, Number(value ?? 0) - 1));

    const buildEnd = (value) =>
        endOfDay(subDays(periodEnd, Number(value ?? 0)));

    return {
        supervisorReviewStartDate: buildStart(config.supervisorReviewPeriodFrom),
        supervisorReviewEndDate: buildEnd(config.supervisorReviewPeriodTo),
        adminApprovalStartDate: buildStart(config.adminApprovalPeriodFrom),
        adminApprovalEndDate: buildEnd(config.adminApprovalPeriodTo),
    };
};
